import { useEffect, FC, useMemo } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

import styles from './styles.less';

const svgPath = (start: Point, end: Point): string => {
    const widthDiff = Math.abs(start.x - end.x);
    const heightDiff = Math.abs(end.y - start.y);
    const third = widthDiff / 3; // треть ширины
    const sixth = third / 2;

    const downwards = end.y > start.y;

    // красивый угол не работает если точки слишком близко по y
    // поэтому подменяем простой кривой на маленьких delta y;
    if (heightDiff - third < 0) {
        return `
        M ${start.x},${start.y}
        L ${start.x + third},${start.y}
        C ${start.x + third + third},${start.y} ${start.x + third},${end.y} ${start.x + third + third},${end.y}
        L ${end.x},${end.y}
        `;
    }

    // вертикальная линия 1/3 ширины
    // кривая 1/6 ширины и высоты
    // горизонтальная линия
    // кривая 1/6 ширины и высоты
    // горизонтальная линия
    return `
        M ${start.x},${start.y}
        L ${start.x + third},${start.y}
        Q ${start.x + third + sixth},${start.y} ${start.x + third + sixth},${start.y + (downwards ? +sixth : -sixth)}
        L ${start.x + third + sixth},${end.y - (downwards ? sixth : -sixth)}
        Q ${start.x + third + sixth},${end.y} ${start.x + third + third},${end.y}
        L ${end.x},${end.y}
        `;
};

export interface Point {
    x: number;
    y: number;
}

export interface CurvyArrowProps {
    start: HTMLElement;
    end: HTMLElement;
    isActive?: boolean;
    debug?: boolean;
    host: HTMLElement;
}

const getPointsInHostCoordSystem = (host: HTMLElement, start: HTMLElement, end: HTMLElement): [Point, Point] => {
    const hostRect = host.getBoundingClientRect();
    const startRect = start.getBoundingClientRect();
    const endRect = end.getBoundingClientRect();
    // переводим в систему координат хоста
    return [
        {
            x: startRect.x - hostRect.x + startRect.width,
            y: startRect.y - hostRect.y,
        },
        {
            x: endRect.x - hostRect.x,
            y: endRect.y - hostRect.y,
        },
    ];
};

const getActualPointsWithBias = (start: Point, end: Point, biasYstart: number, biasYend: number): [Point, Point] => {
    return [
        {
            x: 0,
            y: biasYstart + start.y,
        },
        { x: end.x - start.x, y: end.y - start.y + biasYend + start.y },
    ];
};

const CurvyArrow: FC<CurvyArrowProps> = ({ host, start, end, isActive }) => {
    const [Hstart, Hend] = getPointsInHostCoordSystem(host, start, end);
    const [startWithBias, endWithBias] = getActualPointsWithBias(
        Hstart,
        Hend,
        start.getBoundingClientRect().height / 2,
        end.getBoundingClientRect().height / 2
    );
    const height = useMemo(() => host.getBoundingClientRect().height, [host]);
    const width = endWithBias.x + 10; // с запасом чтобы было видно circle

    const d = svgPath(startWithBias, endWithBias);

    useEffect(() => {
        host.style.position = 'relative';
    }, [host]);

    return createPortal(
        <figure
            className={classNames(styles.curvyArrow, { [styles.active]: isActive })}
            style={{
                left: Math.min(Hstart.x, Hend.x),
                width,
                height,
            }}
        >
            <svg
                fill="none"
                height={height}
                width={width}
                xmlns="http://www.w3.org/2000/svg"
                viewBox={`0 0 ${width} ${height}`}
            >
                <path d={d} fill="transparent" />
                <circle cx={endWithBias.x} cy={endWithBias.y} r="3px" />
            </svg>
        </figure>,
        host
    );
};

export default CurvyArrow;
